// Production Schedule shell. Owns the data fetches, modal state,
// and view dispatch. Filters/sort/group live in URL search params
// (FilterBar mutates them; this shell observes them).
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useAutoImport } from "./hooks/useAutoImport";
import {
  useCapacity,
  useJobs,
  useSolutionTypes,
  useTeam,
} from "./hooks/useProductionApi";
import type { GroupBy, JobDto } from "../../../../../shared/types/production-schedule";
import { KpiCards } from "./components/KpiCards";
import { ViewTabs, type ProductionView } from "./components/ViewTabs";
import { FilterBar } from "./components/FilterBar";
import { BoardView } from "./views/BoardView";
import { ListView } from "./views/ListView";
import { SpoolBoardView } from "./views/SpoolBoardView";
import { SpoolListView } from "./views/SpoolListView";
import { EntityToggle, type EntityMode } from "./components/EntityToggle";
import { GanttView } from "./views/GanttView";
import { BaysView } from "./views/BaysView";
import { CuttingView } from "./views/CuttingView";
import { ResourceView } from "./views/ResourceView";
import { AnalyticsView } from "./views/AnalyticsView";
import { JobModal } from "./modals/JobModal";
import { TeamModal } from "./modals/TeamModal";
import { SolutionTypesModal } from "./modals/SolutionTypesModal";
import { CutOptimizerModal } from "./modals/CutOptimizerModal";
import { applyClientSort } from "./shell/sort";
import { ProductionPageHeader } from "./shell/PageHeader";
import { StagesProvider } from "./components/StagesProvider";
import { JobStatusesProvider } from "./components/JobStatusesProvider";

const VALID_VIEWS: ProductionView[] = [
  "board", "gantt", "bays", "cutting", "list", "resource", "analytics",
];

const FILTER_KEYS = [
  "status", "priority", "solutionTypeId", "assigneeId", "bay", "q", "from", "to",
];

export function ProductionSchedulePage() {
  const params = useParams<{ view?: string; jobId?: string }>();
  const view: ProductionView = VALID_VIEWS.includes(params.view as ProductionView)
    ? (params.view as ProductionView)
    : "board";
  // Deep link from a printed QR — /tools/production-schedule/job/:jobId
  // opens the job modal on mount. ?spool=:id scrolls and pulses the
  // matching row inside the spool table for the workshop.
  const deepLinkJobId = params.jobId ? Number(params.jobId) : null;

  const [search] = useSearchParams();
  const deepLinkSpoolRaw = params.jobId ? search.get("spool") : null;
  const deepLinkSpoolId = deepLinkSpoolRaw ? Number(deepLinkSpoolRaw) : null;
  const [focusSpoolId, setFocusSpoolId] = useState<number | null>(null);
  // Views that host the FilterBar. The 'All Active' default only
  // makes sense where the user can override it from the dropdown;
  // /analytics depends on terminal jobs for its completed-MTD
  // rollups, and a deep-linked QR scan must always be able to open
  // its target job even when terminal. Both bypass activeOnly so
  // they still see the unfiltered job set.
  const filterAwareView = view !== "analytics";
  const apiFilterSearch = useMemo(() => {
    const next = new URLSearchParams();
    for (const k of FILTER_KEYS) {
      const v = search.get(k);
      if (v) next.set(k, v);
    }
    // Status dropdown sentinel mapping:
    //   missing → 'All Active' (default): excludes terminal statuses
    //   '__all' → 'All Statuses': no status filter
    //   <label> → exact match (the existing behaviour)
    const status = next.get("status");
    if (!status && filterAwareView && !deepLinkJobId) {
      next.set("activeOnly", "true");
    } else if (status === "__all") {
      next.delete("status");
    }
    return next.toString();
  }, [search, filterAwareView, deepLinkJobId]);

  const jobsQuery = useJobs(apiFilterSearch);
  const teamQuery = useTeam();
  const typesQuery = useSolutionTypes();
  const capacityQuery = useCapacity();

  const [openJob, setOpenJob] = useState<JobDto | null>(null);
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const autoImport = useAutoImport(search);
  const [teamOpen, setTeamOpen] = useState(false);
  const [typesOpen, setTypesOpen] = useState(false);
  const [cutOptOpen, setCutOptOpen] = useState(false);

  const jobs = jobsQuery.data?.jobs ?? [];
  const team = teamQuery.data?.team ?? [];
  const types = typesQuery.data?.types ?? [];
  const sort = search.get("sort") ?? "priority";
  const group = (search.get("group") as GroupBy) || "status";
  // Toggle defaults to "spools" — the workshop's primary unit of work.
  // Persisted in the URL via ?entity=jobs when the user flips it.
  const entity: EntityMode = search.get("entity") === "jobs" ? "jobs" : "spools";
  const supportsEntityToggle = view === "board" || view === "list";
  const sorted = useMemo(() => applyClientSort(jobs, sort), [jobs, sort]);
  // Spool-entity views show individual spools. Simple-mode jobs
  // track one job-level pipeline instead, so their spools aren't
  // independently trackable — exclude them from the spool board /
  // list (the jobs still appear in the job-entity views). The
  // per-spool activeOnly filter (skip all-complete spools) lives
  // inside each spool view, under StagesProvider, so the fetched
  // catalogue drives isSpoolComplete.
  const spoolEntityJobs = useMemo(() => sorted.filter((j) => !j.simpleMode), [sorted]);
  // Filter context passed into the spool views: true when the URL
  // doesn't pin a specific status (the new 'All Active' default).
  // Also off on analytics / deep-link routes — same reason the API
  // call there stays unfiltered.
  const spoolActiveOnly = !search.get("status") && filterAwareView && !deepLinkJobId;

  const openCreate = () => {
    setOpenJob(null);
    autoImport.clear();
    setJobModalOpen(true);
  };

  useEffect(() => {
    if (autoImport.initial) setJobModalOpen(true);
  }, [autoImport.initial]);

  // Open the deep-linked job once the jobs query resolves. Set the
  // spool focus alongside so the modal can scroll the right row into
  // view as soon as it mounts.
  useEffect(() => {
    if (!deepLinkJobId) return;
    if (jobModalOpen && openJob?.id === deepLinkJobId) return;
    const j = jobs.find((x) => x.id === deepLinkJobId);
    if (j) {
      setOpenJob(j);
      setJobModalOpen(true);
      setFocusSpoolId(deepLinkSpoolId);
    }
  }, [deepLinkJobId, deepLinkSpoolId, jobs, jobModalOpen, openJob?.id]);

  // Keep the modal's job snapshot in sync with the latest jobs query.
  // patchSpoolStage triggers a refetch via onStageChanged → onSaved →
  // refetchAll, but the modal's openJob was set once at open time —
  // without this sync, stage edits (status, eye-toggle, hours, dates)
  // landed on the server but the panel kept rendering the stale rows.
  useEffect(() => {
    if (!jobModalOpen || !openJob) return;
    const fresh = jobs.find((x) => x.id === openJob.id);
    if (fresh && fresh !== openJob) setOpenJob(fresh);
  }, [jobs, jobModalOpen, openJob]);
  const openEdit = (id: number, spoolId?: number) => {
    const j = jobs.find((x) => x.id === id);
    if (j) {
      setOpenJob(j);
      setJobModalOpen(true);
      if (typeof spoolId === "number") setFocusSpoolId(spoolId);
    }
  };
  const refetchAll = async () => {
    await Promise.all([
      jobsQuery.refetch(),
      teamQuery.refetch(),
      typesQuery.refetch(),
      capacityQuery.refetch(),
    ]);
  };

  const loading = jobsQuery.loading || teamQuery.loading || typesQuery.loading;
  const error = jobsQuery.error ?? teamQuery.error ?? typesQuery.error;

  return (
    // AppShell already wraps children in <main className="bsp-content-wide">;
    // a plain fragment here avoids nesting <main> inside <main>.
    <StagesProvider>
      <JobStatusesProvider>
      <ProductionPageHeader
        jobs={jobs}
        onNewJob={openCreate}
        onManageTeam={() => setTeamOpen(true)}
        onCutOptimiser={() => setCutOptOpen(true)}
      />
      {error && (
        <div className="bsp-card" style={{ borderColor: "var(--bsp-red)", marginBottom: 14 }}>
          <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>{error}</p>
        </div>
      )}
      <KpiCards jobs={jobs} />
      <ViewTabs active={view} search={search.toString()} />
      <FilterBar
        team={team}
        types={types}
        showGroup={view === "board" && entity === "jobs"}
        searchPlaceholder={entity === "spools" && supportsEntityToggle ? "Search spools…" : "Search jobs…"}
        leading={supportsEntityToggle ? <EntityToggle active={entity} /> : null}
      />
      {loading && jobs.length === 0 && <p>Loading…</p>}
      {view === "board" && entity === "jobs" && (
        <BoardView
          jobs={sorted}
          team={team}
          types={types}
          groupBy={group}
          onOpenJob={openEdit}
          onJobUpdated={refetchAll}
        />
      )}
      {view === "board" && entity === "spools" && (
        <SpoolBoardView
          jobs={spoolEntityJobs}
          activeOnly={spoolActiveOnly}
          onOpenJob={openEdit}
          onSpoolUpdated={refetchAll}
        />
      )}
      {view === "list" && entity === "jobs" && (
        <ListView jobs={sorted} onOpenJob={openEdit} />
      )}
      {view === "list" && entity === "spools" && (
        <SpoolListView jobs={spoolEntityJobs} team={team} activeOnly={spoolActiveOnly} onOpenJob={openEdit} />
      )}
      {view === "gantt" && <GanttView jobs={sorted} onOpenJob={openEdit} />}
      {view === "bays" && (
        <BaysView jobs={sorted} capacity={capacityQuery.data?.bays ?? []} onOpenJob={openEdit} />
      )}
      {view === "cutting" && <CuttingView jobs={sorted} onOpenJob={openEdit} />}
      {view === "resource" && (
        <ResourceView onOpenJob={openEdit} />
      )}
      {view === "analytics" && (
        // Analytics ignores client-side filters/sort — it answers
        // workbook-level "where's the money" questions across every
        // job, so use the unfiltered jobs list straight from the
        // query.
        <AnalyticsView jobs={jobs} onOpenJob={openEdit} />
      )}
      <JobModal
        open={jobModalOpen}
        job={openJob}
        initial={autoImport.initial}
        team={team}
        types={types}
        focusSpoolId={focusSpoolId}
        onClose={() => { setJobModalOpen(false); autoImport.clear(); setFocusSpoolId(null); }}
        onSaved={() => void refetchAll()}
        onManageTypes={() => setTypesOpen(true)}
      />
      <TeamModal open={teamOpen} onClose={() => { setTeamOpen(false); void teamQuery.refetch(); }} />
      <SolutionTypesModal open={typesOpen} onClose={() => { setTypesOpen(false); void typesQuery.refetch(); }} />
      <CutOptimizerModal open={cutOptOpen} jobs={jobs} onClose={() => setCutOptOpen(false)} />
      </JobStatusesProvider>
    </StagesProvider>
  );
}
